import { core } from "@tauri-apps/api";
import { format, subDays } from "date-fns";
import { useEffect, useState, useMemo } from "react";

// WHAT: Oura-Vascular-Age-Karte mit aktuellem Wert, Altersvergleich und zeitlicher Verlaufsgrafik
// WHY:  Macht vaskuläre Gesundheit (VO2max-basiert) im Health-Dashboard sichtbar und vergleicht sie mit dem realen Alter
// DOMAIN: health → oura → molecules
// DEPENDENCIES: Tauri `core.invoke('get_birthdate'/'sync_oura_history')`, Oura-Hooks (`useOptimizedVascular`, `useOptimizedVascularHistory`),
//              TracQ `Card`/`Badge`, `LineChart`, `date-fns` für Zeitbereich
// RISKS:
//   - NOTE: Nutzt VO2max-basierte Interpretation als „vascular age“; Änderungen am Backend-Modell müssen hier reflektiert werden
//   - NOTE: Chart-Bereich (`chartRange`) wird lokal verwaltet und nicht persistent gespeichert
import { LineChart } from "@/components/charts/primitives/LineChart";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { useOptimizedVascular } from "@/domains/health/oura/hooks/useOptimizedVascular";
import { useOptimizedVascularHistory } from "@/domains/health/oura/hooks/useOptimizedVascularHistory";

/**
 * WHAT:
 *   Vascular age card component displaying current vascular age, age comparison, and historical trend chart.
 *
 * WHY:
 *   Makes vascular health (VO2max-based) visible in health dashboard and compares it with real age.
 *
 * PROPS:
 *   None (uses hooks for data)
 *
 * BEHAVIOR:
 *   - Fetches vascular age from Oura API
 *   - Fetches birthdate for age comparison
 *   - Displays historical trend chart with configurable date range
 *   - Shows age difference badge (younger/older than real age)
 *   - Provides chart range selector (7, 30, 90, 365 days)
 *
 * RISKS:
 *   - NOTE: Uses VO2max-based interpretation as "vascular age" - backend model changes must be reflected here
 *   - NOTE: Chart range is managed locally and not persisted
 *   - WARN: Birthdate fetch may fail - component handles error but may show incorrect comparison
 *
 * @returns Vascular age card component
 *
 */
export function VascularAgeCard() {
  const { error, loading, vascularAge } = useOptimizedVascular();
  const [birthdate, setBirthdate] = useState<null | string>(null);
  const [, setBirthdateError] = useState<null | string>(null);
  const [chartRange, setChartRange] = useState(30);

  // NOTE: Calculate date range for chart - end date is today, start date is chartRange days ago
  const endDate = format(new Date(), "yyyy-MM-dd");
  const startDate = format(subDays(new Date(), chartRange), "yyyy-MM-dd");

  // Fetch historical data
  const { data: vascularHistory, loading: historyLoading } = useOptimizedVascularHistory(
    startDate,
    endDate,
  );

  useEffect(() => {
    const loadBirthdate = async () => {
      try {
        const result = await core.invoke<null | string>("get_birthdate");
        setBirthdate(result);
      } catch (error_) {
        setBirthdateError((error_ as Error).message);
      }
    };

    void loadBirthdate();
  }, []);

  // Process chart data
  const chartData = useMemo(() => {
    if (vascularHistory.length === 0) {
      return [];
    }

    return vascularHistory.map((item) => ({
      day: format(new Date(item.day), "MMM dd"),
      fullDate: item.day,
      vascularAge: item.vascular_age,
    }));
  }, [vascularHistory]);

  if (loading) {
    return <p className="text-sm text-neutral-500">Loading vascular age…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-400">{error}</p>;
  }

  if (vascularAge === null) {
    return (
      <Card className="flex flex-col items-center gap-4 text-center" padding="lg" variant="default">
        <h2 className="text-2xl font-semibold text-neutral-200">Vascular Age</h2>
        <p className="text-sm text-neutral-400 text-center">⚠️ No vascular age available.</p>
        <button
          className="px-4 py-2 bg-neutral-700 text-neutral-100 rounded-lg hover:bg-neutral-600 transition-colors"
          onClick={() => {
            void (async () => {
              try {
                await core.invoke("sync_oura_history");
                globalThis.location.reload(); // Simple refresh for now
              } catch {
                // NOTE: Failed to sync vascular age - silently ignore
              }
            })();
          }}
        >
          🔄 Sync Data
        </button>
      </Card>
    );
  }

  const realAge = birthdate ? calculateAgeFromBirthdate(birthdate) : undefined;
  const vascularAgeValue = vascularAge.vo2_max ?? 0;
  const delta = realAge === undefined ? null : realAge - vascularAgeValue;
  const isYounger = delta !== null && delta > 0;

  return (
    <Card padding="lg" variant="default">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-neutral-200">Vascular Age</h3>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 text-sm rounded ${
              chartRange === 7
                ? "bg-blue-600 text-white"
                : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
            }`}
            onClick={() => {
              setChartRange(7);
            }}
          >
            7d
          </button>
          <button
            className={`px-3 py-1 text-sm rounded ${
              chartRange === 14
                ? "bg-blue-600 text-white"
                : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
            }`}
            onClick={() => {
              setChartRange(14);
            }}
          >
            14d
          </button>
          <button
            className={`px-3 py-1 text-sm rounded ${
              chartRange === 30
                ? "bg-blue-600 text-white"
                : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
            }`}
            onClick={() => {
              setChartRange(30);
            }}
          >
            30d
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Top Section - Current Data */}
        <div className="flex items-center justify-center space-x-8">
          <div className="text-center">
            <p className="text-4xl font-bold text-neutral-100 mb-2">
              {vascularAgeValue} <span className="text-lg font-normal">years</span>
            </p>
            <p className="text-sm text-neutral-400">Vascular Age</p>
          </div>

          {realAge !== undefined && (
            <div className="text-center">
              <Badge className="text-sm" variant={isYounger ? "success" : "destructive"}>
                {isYounger
                  ? `Younger by ${String(delta)}y`
                  : `Older by ${String(Math.abs(delta ?? 0))}y`}
              </Badge>
              <p className="text-sm text-neutral-400 mt-1">vs Real Age</p>
            </div>
          )}

          {vascularAge.timestamp ? (
            <div className="text-center">
              <p className="text-sm text-neutral-400">
                {format(new Date(vascularAge.timestamp), "MMM d, yyyy")}
              </p>
              <p className="text-sm text-neutral-400">Last measured</p>
            </div>
          ) : null}
        </div>

        {/* Bottom Section - Chart (Full Width) */}
        <div className="space-y-4">
          {chartData.length > 0 ? (
            <LineChart
              className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
              color="#ffffff"
              data={chartData}
              gridLines={{
                horizontal: true,
                horizontalColor: "rgba(255, 255, 255, 0.1)",
                horizontalValues: [],
                vertical: false,
              }}
              height={250}
              xKey="day"
              yKey="vascularAge"
            />
          ) : (
            <div className="h-[250px] flex items-center justify-center bg-neutral-800 rounded-lg">
              <p className="text-sm text-neutral-400">
                {historyLoading ? "Loading chart data..." : "No historical data available"}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * WHAT:
 *   Calculates age from birthdate string.
 *
 * WHY:
 *   Provides accurate age calculation for comparison with vascular age.
 *
 * @param birthdate - Birthdate string in ISO format
 *
 * @returns Calculated age in years
 *
 */
function calculateAgeFromBirthdate(birthdate: string): number {
  const birth = new Date(birthdate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();

  const hasBirthdayPassed =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return age;
}
